<script setup lang="ts">
import { computed, ref, withDefaults } from 'vue';
import Chart from 'primevue/chart';
import type { PRData } from '@/backend/metrics.backend';
import { DateTime } from 'luxon';
import Skeleton from 'primevue/skeleton';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';

interface Props {
  from: Date;
  to: Date;
  prData: PRData[];
  loading: boolean;
  error: string | null;
  groupBy?: 'day' | 'week';
  locGroupBy?: 'additions-deletions' | 'repo' | 'github-username' | 'ai-contribution' | 'team';
}

const props = withDefaults(defineProps<Props>(), {
  groupBy: 'day',
  locGroupBy: 'additions-deletions'
});

const emit = defineEmits<{
  'update:groupBy': [value: 'day' | 'week'];
  'update:locGroupBy': [value: 'additions-deletions' | 'repo' | 'github-username' | 'ai-contribution' | 'team'];
  retry: [];
}>();

// Internal state for controls (using props with emit for two-way binding)
const groupBy = computed({
  get: () => props.groupBy,
  set: (value) => emit('update:groupBy', value)
});

const locGroupBy = computed({
  get: () => props.locGroupBy,
  set: (value) => emit('update:locGroupBy', value)
});

// Helper function to group PRs by date (day or week)
function groupByDate(prs: PRData[], groupByMode: 'day' | 'week'): Map<string, PRData[]> {
  const groups = new Map<string, PRData[]>();
  
  prs.forEach(pr => {
    const date = DateTime.fromISO(pr.dateMerged);
    let dateKey: string;
    
    if (groupByMode === 'week') {
      // Use Monday as start of week (ISO week)
      dateKey = date.startOf('week').toISODate() || pr.dateMerged;
    } else {
      dateKey = pr.dateMerged;
    }
    
    if (!groups.has(dateKey)) {
      groups.set(dateKey, []);
    }
    groups.get(dateKey)!.push(pr);
  });
  
  return groups;
}

// Helper function to format date labels
function formatDateLabel(dateKey: string, groupByMode: 'day' | 'week'): string {
  const date = DateTime.fromISO(dateKey);
  
  if (groupByMode === 'week') {
    return `Week of ${date.toFormat('MMM dd, yyyy')}`;
  } else {
    return date.toFormat('MMM dd, yyyy');
  }
}

// Helper function to format AI contribution labels
function formatAiContributionLabel(aiContribution: string): string {
  const labels: Record<string, string> = {
    'none': 'None',
    'research': 'Research Only',
    'minor': 'Minor (≤20%)',
    'moderate': 'Moderate (20-40%)',
    'major': 'Major (40-60%)',
    'most': 'Most (60-80%)',
    'primary': 'Primary (>80%)',
    'tests-only': 'Tests Only',
    'review': 'Review Only',
    'other': 'Other'
  };
  return labels[aiContribution] || aiContribution;
}

// Computed property to process raw data based on grouping options
const processedData = computed(() => {
  // Generate all dates in the range based on grouping mode
  const fromDate = DateTime.fromJSDate(props.from);
  const toDate = DateTime.fromJSDate(props.to);
  const allDates: string[] = [];
  
  if (groupBy.value === 'week') {
    // Generate all weeks in the range
    let current = fromDate.startOf('week');
    const end = toDate.startOf('week');
    
    while (current <= end) {
      allDates.push(current.toISODate()!);
      current = current.plus({ weeks: 1 });
    }
  } else {
    // Generate all days in the range
    let current = fromDate.startOf('day');
    const end = toDate.startOf('day');
    
    while (current <= end) {
      allDates.push(current.toISODate()!);
      current = current.plus({ days: 1 });
    }
  }
  
  // If no data, return empty groups for all dates
  if (!props.prData.length) {
    const groups = new Map<string, Map<string, { additions: number; deletions: number }>>();
    allDates.forEach(dateKey => {
      const subGroups = new Map<string, { additions: number; deletions: number }>();
      subGroups.set('additions-deletions', { additions: 0, deletions: 0 });
      groups.set(dateKey, subGroups);
    });
    return { dates: allDates, groups };
  }
  
  // First, group by date (day or week)
  const dateGroups = groupByDate(props.prData, groupBy.value);
  
  // Now group by locGroupBy option within each date
  const groups = new Map<string, Map<string, { additions: number; deletions: number }>>();
  
  allDates.forEach(dateKey => {
    const prsForDate = dateGroups.get(dateKey) || [];
    const subGroups = new Map<string, { additions: number; deletions: number }>();
    
    if (locGroupBy.value === 'repo') {
      // Group by repository
      prsForDate.forEach(pr => {
        const key = pr.repo;
        if (!subGroups.has(key)) {
          subGroups.set(key, { additions: 0, deletions: 0 });
        }
        const group = subGroups.get(key)!;
        group.additions += pr.additions;
        group.deletions += pr.deletions;
      });
    } else if (locGroupBy.value === 'github-username') {
      // Group by username
      prsForDate.forEach(pr => {
        const key = pr.username;
        if (!subGroups.has(key)) {
          subGroups.set(key, { additions: 0, deletions: 0 });
        }
        const group = subGroups.get(key)!;
        group.additions += pr.additions;
        group.deletions += pr.deletions;
      });
    } else if (locGroupBy.value === 'team') {
      // Group by team name
      prsForDate.forEach(pr => {
        const key = pr.teamName || 'No Team';
        if (!subGroups.has(key)) {
          subGroups.set(key, { additions: 0, deletions: 0 });
        }
        const group = subGroups.get(key)!;
        group.additions += pr.additions;
        group.deletions += pr.deletions;
      });
    } else if (locGroupBy.value === 'ai-contribution') {
      // Group by AI vs Human contribution
      let aiLoc = 0;
      let humanLoc = 0;
      
      prsForDate.forEach(pr => {
        aiLoc += pr.aiLoc || 0;
        humanLoc += pr.totalLoc - (pr.aiLoc || 0);
      });
      
      subGroups.set('ai', { additions: aiLoc, deletions: 0 });
      subGroups.set('human', { additions: humanLoc, deletions: 0 });
    } else {
      // Default: just sum all additions and deletions
      const totals = prsForDate.reduce(
        (acc, pr) => ({
          additions: acc.additions + pr.additions,
          deletions: acc.deletions + pr.deletions
        }),
        { additions: 0, deletions: 0 }
      );
      subGroups.set('additions-deletions', totals);
    }
    
    groups.set(dateKey, subGroups);
  });
  
  return { dates: allDates, groups };
});

// Chart data computed property
const chartData = computed(() => {
  const { dates, groups } = processedData.value;
  
  if (!dates.length) return null;

  const labels = dates.map(dateKey => formatDateLabel(dateKey, groupBy.value));

  if (locGroupBy.value === 'additions-deletions') {
    // Default: show additions and deletions as separate datasets
    const additionsData = dates.map(dateKey => {
      const group = groups.get(dateKey)?.get('additions-deletions');
      return group?.additions || 0;
    });
    
    const deletionsData = dates.map(dateKey => {
      const group = groups.get(dateKey)?.get('additions-deletions');
      return group?.deletions || 0;
    });

    return {
      labels,
      datasets: [
        {
          label: 'Additions',
          data: additionsData,
          backgroundColor: 'rgba(34,197,94,0.35)',
          borderColor: 'rgba(34,197,94,1)',
          fill: 'origin',
          stack: 'loc',
          tension: 0.1
        },
        {
          label: 'Deletions',
          data: deletionsData,
          backgroundColor: 'rgba(239,68,68,0.35)',
          borderColor: 'rgba(239,68,68,1)',
          fill: 'origin', 
          stack: 'loc',
          tension: 0.1
        }
      ]
    };
  } else if (locGroupBy.value === 'ai-contribution') {
    // AI contribution: show AI LOC vs Human LOC
    const aiLocData = dates.map(dateKey => {
      const group = groups.get(dateKey)?.get('ai');
      return group?.additions || 0;
    });
    
    const humanLocData = dates.map(dateKey => {
      const group = groups.get(dateKey)?.get('human');
      return group?.additions || 0;
    });

    return {
      labels,
      datasets: [
        {
          label: 'AI LOC',
          data: aiLocData,
          backgroundColor: 'rgba(168,85,247,0.5)',
          borderColor: 'rgba(168,85,247,1)',
          fill: 'origin',
          stack: 'loc',
          tension: 0.1
        },
        {
          label: 'Human LOC',
          data: humanLocData,
          backgroundColor: 'rgba(59,130,246,0.5)',
          borderColor: 'rgba(59,130,246,1)',
          fill: 'origin',
          stack: 'loc',
          tension: 0.1
        }
      ]
    };
  } else {
    // Group by repo or username: show total LOC for each group
    // Collect all unique group keys across all dates
    const allGroupKeys = new Set<string>();
    groups.forEach((dateGroups: Map<string, { additions: number; deletions: number }>) => {
      dateGroups.forEach((_: { additions: number; deletions: number }, key: string) => allGroupKeys.add(key));
    });
    
    const sortedGroupKeys = Array.from(allGroupKeys).sort();
    
    // Define colors for different groups
    const colors = [
      { bg: 'rgba(59,130,246,0.5)', border: 'rgba(59,130,246,1)' },    // blue
      { bg: 'rgba(34,197,94,0.5)', border: 'rgba(34,197,94,1)' },      // green
      { bg: 'rgba(168,85,247,0.5)', border: 'rgba(168,85,247,1)' },    // purple
      { bg: 'rgba(251,146,60,0.5)', border: 'rgba(251,146,60,1)' },    // orange
      { bg: 'rgba(236,72,153,0.5)', border: 'rgba(236,72,153,1)' },    // pink
      { bg: 'rgba(14,165,233,0.5)', border: 'rgba(14,165,233,1)' },    // cyan
      { bg: 'rgba(234,179,8,0.5)', border: 'rgba(234,179,8,1)' },      // yellow
      { bg: 'rgba(239,68,68,0.5)', border: 'rgba(239,68,68,1)' },      // red
    ];
    
    const datasets = sortedGroupKeys.map((groupKey, idx) => {
      const data = dates.map(dateKey => {
        const group = groups.get(dateKey)?.get(groupKey);
        return group ? (group.additions + group.deletions) : 0;
      });
      
      const color = colors[idx % colors.length];
      // Format label for AI contribution grouping
      const label = locGroupBy.value === 'ai-contribution' 
        ? formatAiContributionLabel(groupKey)
        : groupKey;
      
      return {
        label,
        data,
        backgroundColor: color.bg,
        borderColor: color.border,
        fill: 'origin',
        stack: 'loc',
        tension: 0.1
      };
    });
    
    return { labels, datasets };
  }
});

// Chart options
const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index' as const,
    intersect: false,
  },
  plugins: {
    legend: {
      position: 'top' as const,
      onClick: (evt: any, legendItem: any) => {
        // Toggle dataset visibility
        const chart = evt.chart;
        const index = legendItem.datasetIndex;
        const meta = chart.getDatasetMeta(index);
        meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;
        chart.update();
      }
    },
    tooltip: {
      itemSort: (a: any, b: any) => {
        // Sort tooltip items by value, highest to lowest
        return b.parsed.y - a.parsed.y;
      },
      callbacks: {
        title: (context: any) => {
          const dateKey = processedData.value.dates[context[0].dataIndex];
          const date = DateTime.fromISO(dateKey);
          
          if (locGroupBy.value === 'additions-deletions') {
            const group = processedData.value.groups.get(dateKey)?.get('additions-deletions');
            const total = group ? (group.additions + group.deletions) : 0;
            
            if (groupBy.value === 'week') {
              return `Week of ${date.toFormat('cccc, MMM dd, yyyy')}: ${total.toLocaleString()}`;
            } else {
              return `${date.toFormat('cccc, MMM dd, yyyy')}: ${total.toLocaleString()}`;
            }
          } else {
            return chartData.value?.labels?.[context[0].dataIndex] || '';
          }
        },
        footer: (context: any) => {
          if (locGroupBy.value === 'additions-deletions' && context.length >= 2) {
            const dateKey = processedData.value.dates[context[0].dataIndex];
            const group = processedData.value.groups.get(dateKey)?.get('additions-deletions');
            
            if (group) {
              const net = group.additions - group.deletions;
              const total = group.additions + group.deletions;
              const deletionRatio = total > 0 ? Math.round((group.deletions / total) * 100) : 0;
              
              return [
                `Total: ${total.toLocaleString()}`,
                `Net: ${net > 0 ? '+' : ''}${net.toLocaleString()}`,
                `Deletions: ${deletionRatio}% of total`
              ];
            }
          }
          return '';
        }
      }
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      stacked: true,
      ticks: {
        callback: function(value: any) {
          // Format large numbers (e.g., 1.2k)
          const num = typeof value === 'number' ? value : parseFloat(value);
          if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'k';
          }
          return num.toString();
        }
      }
    },
    x: {
      stacked: true
    }
  }
}));

// Retry function
function retry() {
  emit('retry');
}

// Computed properties for accessibility and summary
const totalStats = computed(() => {
  if (!props.prData.length) return null;
  
  const totals = props.prData.reduce(
    (acc: { additions: number; deletions: number }, pr: PRData) => ({
      additions: acc.additions + pr.additions,
      deletions: acc.deletions + pr.deletions
    }),
    { additions: 0, deletions: 0 }
  );
  
  const dates = processedData.value.dates;
  
  return {
    ...totals,
    net: totals.additions - totals.deletions,
    total: totals.additions + totals.deletions,
    days: dates.length
  };
});

// Summary table data - aggregated stats for the whole period
interface SummaryRow {
  name: string;
  additions: number;
  deletions: number;
  total: number;
  net: number;
  percentage: number;
}

const summaryTableData = computed<SummaryRow[]>(() => {
  if (!props.prData.length) return [];
  
  const aggregated = new Map<string, { additions: number; deletions: number }>();
  
  if (locGroupBy.value === 'repo') {
    // Aggregate by repository
    props.prData.forEach(pr => {
      const key = pr.repo;
      if (!aggregated.has(key)) {
        aggregated.set(key, { additions: 0, deletions: 0 });
      }
      const entry = aggregated.get(key)!;
      entry.additions += pr.additions;
      entry.deletions += pr.deletions;
    });
  } else if (locGroupBy.value === 'github-username') {
    // Aggregate by username
    props.prData.forEach(pr => {
      const key = pr.username;
      if (!aggregated.has(key)) {
        aggregated.set(key, { additions: 0, deletions: 0 });
      }
      const entry = aggregated.get(key)!;
      entry.additions += pr.additions;
      entry.deletions += pr.deletions;
    });
  } else if (locGroupBy.value === 'team') {
    // Aggregate by team name
    props.prData.forEach(pr => {
      const key = pr.teamName || 'No Team';
      if (!aggregated.has(key)) {
        aggregated.set(key, { additions: 0, deletions: 0 });
      }
      const entry = aggregated.get(key)!;
      entry.additions += pr.additions;
      entry.deletions += pr.deletions;
    });
  } else if (locGroupBy.value === 'ai-contribution') {
    // Aggregate by AI vs Human LOC
    let totalAiLoc = 0;
    let totalHumanLoc = 0;
    
    props.prData.forEach(pr => {
      totalAiLoc += pr.aiLoc || 0;
      totalHumanLoc += pr.totalLoc - (pr.aiLoc || 0);
    });
    
    aggregated.set('AI LOC', { additions: totalAiLoc, deletions: 0 });
    aggregated.set('Human LOC', { additions: totalHumanLoc, deletions: 0 });
  } else {
    // For additions-deletions view, just show overall totals
    const totals = props.prData.reduce(
      (acc, pr) => ({
        additions: acc.additions + pr.additions,
        deletions: acc.deletions + pr.deletions
      }),
      { additions: 0, deletions: 0 }
    );
    aggregated.set('Total', totals);
  }
  
  // Calculate grand total for percentage calculation
  const grandTotal = Array.from(aggregated.values()).reduce(
    (sum, entry) => sum + entry.additions + entry.deletions,
    0
  );
  
  // Convert to array and calculate derived values
  const rows: SummaryRow[] = Array.from(aggregated.entries()).map(([name, stats]) => {
    const total = stats.additions + stats.deletions;
    const net = stats.additions - stats.deletions;
    const percentage = grandTotal > 0 ? (total / grandTotal) * 100 : 0;
    
    return {
      name,
      additions: stats.additions,
      deletions: stats.deletions,
      total,
      net,
      percentage
    };
  });
  
  // Sort by total (descending)
  rows.sort((a, b) => b.total - a.total);
  
  return rows;
});
</script>

<template>
  <div class="loc-chart-container">
    <!-- Loading State -->
    <div v-if="loading" class="chart-skeleton">
      <Skeleton height="400px" />
    </div>
    
    <!-- Error State -->
    <div v-else-if="error" class="error-state text-center py-8">
      <p class="text-red-600 mb-4">{{ error }}</p>
      <Button @click="retry" label="Retry" severity="secondary" />
    </div>
    
    <!-- Chart (shows even with no data, displaying empty x-axis) -->
    <div v-else class="chart-wrapper">
      <div class="chart-canvas">
        <Chart
          type="line"
          :data="chartData"
          :options="chartOptions"
          style="height: 400px; width: 100%;"
          :canvasProps="{
            'role': 'img',
            'aria-label': `${groupBy === 'day' ? 'Daily' : 'Weekly'} lines of code additions and deletions chart`
          }"
        />
      </div>
            
      <!-- Summary Table -->
      <div v-if="summaryTableData.length > 0" class="summary-table-container mt-6">
        <h4 class="text-sm font-semibold mb-3 text-surface-900 dark:text-surface-0">
          Period Summary
          <span v-if="locGroupBy === 'repo'" class="text-muted-color font-normal"> - by Repository</span>
          <span v-else-if="locGroupBy === 'github-username'" class="text-muted-color font-normal"> - by Contributor</span>
          <span v-else-if="locGroupBy === 'team'" class="text-muted-color font-normal"> - by Team</span>
          <span v-else-if="locGroupBy === 'ai-contribution'" class="text-muted-color font-normal"> - AI vs Human</span>
        </h4>
        <DataTable 
          :value="summaryTableData" 
          size="small"
          class="summary-table"
          :pt="{
            root: { class: 'text-sm' }
          }"
        >
          <Column 
            field="name"
            :header="locGroupBy === 'repo' ? 'Repository' : locGroupBy === 'github-username' ? 'Contributor' : locGroupBy === 'team' ? 'Team' : locGroupBy === 'ai-contribution' ? 'Source' : 'Metric'"
            sortable
          >
            <template #body="{ data }">
              <span class="font-medium">{{ data.name }}</span>
            </template>
          </Column>
          <Column v-if="locGroupBy !== 'ai-contribution'" field="additions" header="Additions" sortable class="text-right">
            <template #body="{ data }">
              <span class="text-green-600 dark:text-green-400">+{{ data.additions.toLocaleString() }}</span>
            </template>
          </Column>
          <Column v-if="locGroupBy !== 'ai-contribution'" field="deletions" header="Deletions" sortable class="text-right">
            <template #body="{ data }">
              <span class="text-red-600 dark:text-red-400">-{{ data.deletions.toLocaleString() }}</span>
            </template>
          </Column>
          <Column field="total" :header="locGroupBy === 'ai-contribution' ? 'LOC' : 'Total LOC'" sortable class="text-right">
            <template #body="{ data }">
              <span class="font-semibold">{{ data.total.toLocaleString() }}</span>
            </template>
          </Column>
          <Column v-if="locGroupBy !== 'ai-contribution'" field="net" header="Net Change" sortable class="text-right">
            <template #body="{ data }">
              <span :class="data.net >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                {{ data.net >= 0 ? '+' : '' }}{{ data.net.toLocaleString() }}
              </span>
            </template>
          </Column>
          <Column field="percentage" header="% of Total" sortable class="text-right">
            <template #body="{ data }">
              <span class="text-muted-color">{{ data.percentage.toFixed(1) }}%</span>
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
  </div>
</template>

<style scoped>
.loc-chart-container {
  min-height: 300px;
}

.chart-wrapper {
  position: relative;
}

.chart-canvas {
  position: relative;
  height: 400px;
  width: 100%;
}

.error-state {
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.chart-skeleton {
  min-height: 300px;
}

.text-summary {
  font-family: 'Courier New', monospace;
}

.summary-table-container {
  border-top: 1px solid var(--surface-border);
  padding-top: 1.5rem;
}

.summary-table :deep(.p-datatable-thead > tr > th) {
  background-color: var(--surface-50);
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-color-secondary);
}

.summary-table :deep(.p-datatable-tbody > tr:hover) {
  background-color: var(--surface-100);
}

:global(.dark) .summary-table :deep(.p-datatable-thead > tr > th) {
  background-color: var(--surface-800);
}

:global(.dark) .summary-table :deep(.p-datatable-tbody > tr:hover) {
  background-color: var(--surface-800);
}
</style>
